import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

// GA4 — production only, never for the site owner.
const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const PROD_HOSTS = ['twylightcrow.com', 'www.twylightcrow.com'];
const OPT_OUT_KEY = 'dividrip:ga-opt-out';

function shouldTrack() {
  if (!GA_ID) return false;
  if (typeof window === 'undefined') return false;
  if (!PROD_HOSTS.includes(window.location.hostname)) return false;
  if (navigator.doNotTrack === '1') return false;
  try { if (localStorage.getItem(OPT_OUT_KEY) === '1') return false; } catch { /* ignore */ }
  return true;
}

function loadGtag() {
  if (window.gtag) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { send_page_view: false, anonymize_ip: true });
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  document.head.appendChild(s);
}

// Fire a GA4 event. Safe no-op when tracking is off (preview, owner, DNT).
export function trackEvent(name, params = {}) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', name, params);
}

export default function Analytics() {
  const { pathname, search } = useLocation();
  const { isRealAdmin, loading } = useAuth();
  const enabledRef = useRef(false);

  useEffect(() => {
    if (loading) return;
    if (isRealAdmin) {
      // Remember this browser belongs to the owner so logged-out visits are excluded too.
      try { localStorage.setItem(OPT_OUT_KEY, '1'); } catch { /* ignore */ }
      enabledRef.current = false;
      return;
    }
    if (!shouldTrack()) { enabledRef.current = false; return; }
    loadGtag();
    enabledRef.current = true;
  }, [loading, isRealAdmin]);

  useEffect(() => {
    if (!enabledRef.current || !window.gtag) return;
    window.gtag('event', 'page_view', {
      page_path: pathname + search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, search, loading, isRealAdmin]);

  return null;
}
